import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';

const { FiCircle, FiLayers, FiTool, FiSettings, FiDatabase, FiArrowRight } = FiIcons;

const Categories = () => {
  const { getCategories } = useDatabase();
  const categories = getCategories();

  const categoryIcons = {
    'EC000030': FiCircle,
    'EC000031': FiCircle,
    'EC000032': FiCircle,
    'EC000033': FiCircle,
    'EC000034': FiCircle,
    'EC000035': FiCircle,
    'EC000924': FiTool,
    'EC002306': FiLayers,
    'EC002307': FiLayers,
    'EC001234': FiSettings,
    'EC001235': FiSettings
  };

  const getCategoryDescription = (code) => {
    const descriptions = {
      'EC000030': 'Standard-Rotationslager für radiale Belastungen mit hoher Zuverlässigkeit und Präzision',
      'EC000031': 'Speziallager für kombinierte radial-axiale Belastungen in anspruchsvollen Anwendungen',
      'EC000032': 'Kompakte Nadellager für hohe Tragkraft bei minimalem Bauraum',
      'EC000033': 'Axiallager für reine Schubbelastungen in vertikalen Anwendungen',
      'EC000034': 'Einbaufertige Gehäuselager mit selbstausrichtenden Eigenschaften',
      'EC000035': 'Kegelrollenlager für hohe kombinierte Belastungen und Steifigkeit',
      'EC000924': 'Hochpräzise Linearführungen für industrielle Automatisierung und Positioniersysteme',
      'EC002306': 'Professionelle Abdichtungslösungen für rotierende Wellen und statische Gehäuse',
      'EC002307': 'O-Ringe für statische und dynamische Abdichtungsanwendungen',
      'EC001234': 'Kraftübertragungselemente für Riemenantriebe in verschiedenen Industrieanwendungen',
      'EC001235': 'Zahnriemen für synchrone Kraftübertragung ohne Schlupf'
    };
    return descriptions[code] || 'Industriekomponente für spezielle technische Anwendungen';
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Produktkategorien</h1>
            <p className="text-gray-600 mt-2">
              Durchsuchen Sie Industriekomponenten nach ETIM-Klassifikation
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-100">
            <SafeIcon icon={FiDatabase} className="w-5 h-5" />
            <span className="font-medium">{categories.length} Kategorien</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.code}
              className="category-card bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -4 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                  <SafeIcon
                    icon={categoryIcons[category.code] || FiDatabase}
                    className="w-6 h-6 text-red-600"
                  />
                </div>
                <span className="text-xs font-mono bg-gray-100 text-gray-800 px-2 py-1 rounded border border-gray-200">
                  {category.code}
                </span>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {category.name}
              </h3>

              <p className="text-gray-600 text-sm mb-4">
                {getCategoryDescription(category.code)}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900">{category.count}</span>
                  <span className="text-sm text-gray-600">Produkte</span>
                </div>
                <Link
                  to={`/search?category=${category.code}`}
                  className="flex items-center space-x-2 text-red-600 hover:text-red-700 font-medium group"
                >
                  <span>Durchsuchen</span>
                  <SafeIcon
                    icon={FiArrowRight}
                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Technical Standards Information */}
        <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">ETIM-Klassifikation</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Wälzlagertechnik</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• <strong>EC000030:</strong> Rillenkugellager - Standard Rotationslager für radiale Belastung</li>
                <li>• <strong>EC000031:</strong> Schrägkugellager - Kombinierte radial-axiale Belastung</li>
                <li>• <strong>EC000032:</strong> Nadellager - Kompakte Bauform für hohe Radiallasten</li>
                <li>• <strong>EC000033:</strong> Axiallager - Reine Schubbelastungen</li>
                <li>• <strong>EC000034:</strong> Gehäuselager - Einbaufertige Lösungen</li>
                <li>• <strong>EC000035:</strong> Kegelrollenlager - Hohe kombinierte Belastungen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Weitere Komponenten</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• <strong>EC000924:</strong> Profilschienenführungen - Präzise Linearführungen</li>
                <li>• <strong>EC002306:</strong> Radialwellendichtringe - Abdichtung rotierender Wellen</li>
                <li>• <strong>EC002307:</strong> O-Ringe - Statische und dynamische Abdichtung</li>
                <li>• <strong>EC001234:</strong> Keilriemen - Kraftübertragungselemente</li>
                <li>• <strong>EC001235:</strong> Zahnriemen - Synchrone Kraftübertragung</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Categories;